import React, { useContext, useEffect, useState } from 'react';
import {
  ArrowRightStartOnRectangleIcon,
  Bars3Icon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { MobileNavigationContext } from '../../contexts/MobileContext';
import { Dropdown, MenuProps, theme } from 'antd';
import { useLogoutMutation } from '../../store';
import {
  useLazyGetProfilePictureQuery,
  useGetProfilePictureQuery,
} from '../../store';
import { useAuth, useNotification } from '../../contexts';
import requisitionApi from '../../store/rtkQuery/requisition';
import authApi from '../../store/rtkQuery/auth';
import utilityApi from '../../store/rtkQuery/utility';
import userAccessControlApi from '../../store/rtkQuery/userAccessControl';
import { useAppDispatch } from '../../store/hooks';
import { LockKeyhole, ChevronDown } from 'lucide-react';
import { ClientCode } from '../../enums';
import { useNavigate } from 'react-router-dom';
import denLogo from '../../assets/images/den_logo_image.svg';
import hathLogo from '../../assets/images/hathway_logo.png';
import softgelLogo from '../../assets/images/softgel_logo.png';
import sapphireLogo from '../../assets/images/sapphire_logo.png';
import ChangePasswordModal from '../../components/ChangePasswordModal';
import ChangeProfilePictureModal from '../../components/ChangeProfilePictureModal';
import { GlobalLoader } from '../../components';
import Notifications from '../Notification/Notifications';
import { cn, removeChatbotToken, unloadChatbotScript } from '../../utils';

const { useToken } = theme;

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const {
    authenticationMethod,
    setAuthenticationMethod,
    setIsAuthenticated,
    setUser,
    user,
  } = useAuth();
  const { setIsOpen } = useContext(MobileNavigationContext);
  const [logOut, { isLoading, isError, error, isSuccess }] =
    useLogoutMutation();

  const dispatch = useAppDispatch();
  const { token } = useToken();
  const [changePasswordModalVisible, setChangePasswordModalVisible] =
    useState(false);
  const [
    changeProfilePictureModalVisible,
    setChangeProfilePictureModalVisible,
  ] = useState(false);
  const [getProfilePic] = useLazyGetProfilePictureQuery();
  const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);
  const {
    data: profilePicBlob,
    isSuccess: isProfilePicSuccess,
    refetch: refetchProfilePic,
    isFetching: isProfilePicFetching,
  } = useGetProfilePictureQuery(user?.ID, { skip: !user?.ID });

  const contentStyle: React.CSSProperties = {
    backgroundColor: token.colorBgElevated,
    borderRadius: token.borderRadiusLG,
    boxShadow: token.boxShadowSecondary,
  };

  const menuStyle: React.CSSProperties = {
    boxShadow: 'none',
  };

  const handleLogOutClick = async () => {
    try {
      await logOut().unwrap();
      // Notify other tabs (e.g. device-limit page) that a session slot opened
      try {
        const bc = new BroadcastChannel('session_events');
        bc.postMessage({ type: 'SESSION_LOGOUT' });
        bc.close();
      } catch { /* BroadcastChannel not supported — ignore */ }
      setIsAuthenticated(false);
      setUser(null);
      setAuthenticationMethod(null);
      resetApiState();
      localStorage.removeItem('redirectPath');
      sessionStorage.removeItem('redirected');
      removeChatbotToken();
      localStorage.removeItem('assetToken');
      navigate('/signin', { replace: true });
      unloadChatbotScript();
    } catch (err) {
      console.error(err);
      showNotification('error', 'Error', 'Error in logging out user.');
    }
  };

  const handleChangePasswordClick = () => {
    setChangePasswordModalVisible(true);
  };

  const handleChangeProfilePictureClick = async () => {
    if (profilePicUrl) {
      setChangeProfilePictureModalVisible(true);
      return;
    }
    if (user?.ID) {
      const result = await getProfilePic(user.ID);
      if (result?.data && !result.isError) {
        const url = URL.createObjectURL(result.data);
        setProfilePicUrl(url);
      } else {
        setProfilePicUrl(null);
      }
    } else {
      setProfilePicUrl(null);
    }
    setChangeProfilePictureModalVisible(true);
  };

  const items: MenuProps['items'] = [
    ...(authenticationMethod !== 'KEYCLOAK'
      ? [
        {
          label: (
            <div
              className="flex items-center gap-2"
              onClick={handleChangePasswordClick}
            >
              <LockKeyhole className="size-4" />
              <span>Change Password</span>
            </div>
          ),
          key: '4',
        },
      ]
      : []),
    {
      label: (
        <div
          className="flex items-center gap-2"
          onClick={handleChangeProfilePictureClick}
        >
          <UserCircleIcon className="size-4" />
          <span>Change Profile Picture</span>
        </div>
      ),
      key: '5',
    },
    {
      label: (
        <div
          className="flex items-center gap-2 text-error-300 "
          onClick={handleLogOutClick}
        >
          <ArrowRightStartOnRectangleIcon className="size-4 " />
          <span>Log out</span>
        </div>
      ),
      key: '3',
    },
  ];

  useEffect(() => {
    if (isError && error && !isLoading) {
      showNotification(
        'error',
        'Code : LGN-ERR-02',
        'Error in logging out user. Please try again later',
      );
    }

    if (isSuccess && !isLoading) {
      showNotification(
        'success',
        'Logout Successful',
        'You have been successfully logged out',
      );
    }
  }, [isError, error, isLoading, isSuccess]);

  useEffect(() => {
    if (isProfilePicSuccess && profilePicBlob) {
      const url = URL.createObjectURL(profilePicBlob);
      setProfilePicUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setProfilePicUrl(null);
    }
  }, [isProfilePicSuccess, profilePicBlob]);

  function resetApiState() {
    dispatch(requisitionApi.util.resetApiState());
    dispatch(authApi.util.resetApiState());
    dispatch(utilityApi.util.resetApiState());
    dispatch(userAccessControlApi.util.resetApiState());
  }

  const handleMenuBarClick = () => {
    setIsOpen(true);
  };
  return (
    <>
      <header
        className={cn(
          'sticky inset-x-0 top-0 z-[12] w-full transition-all bg-white/50 backdrop-blur-sm shadow print:hidden',
        )}
      >
        {/* {isProfilePicFetching && <GlobalLoader loading={true} />} */}
        <div className="absolute left-3 md:left-16 bottom-0.5 flex items-center h-full">
          {user?.companyGroup?.clientCode && (
            <img
              src={
                user.companyGroup.clientCode === ClientCode.DEN
                  ? denLogo
                  : user.companyGroup.clientCode === ClientCode.HATHWAY
                    ? hathLogo
                    : user.companyGroup.clientCode === ClientCode.SOFTGEL
                      ? softgelLogo
                      : (user.companyGroup.clientCode === ClientCode.SAPPHIRE || user.companyGroup.clientCode === 'SAPH')
                        ? sapphireLogo
                        : ''
              }
              alt={`${user.companyGroup.clientCode} Logo`}
              className="w-[72px] h-auto object-contain"
            />
          )}
        </div>

        <div className="flex h-12 items-center justify-end ps-4 gap-x-4">
          <Notifications userId={user?.ID} />
          <div className="flex items-center gap-x-1">
            <Dropdown
              trigger={['click']}
              menu={{ items }}
              dropdownRender={menu => (
                <div style={contentStyle}>
                  {React.cloneElement(menu as React.ReactElement, {
                    style: menuStyle,
                  })}
                </div>
              )}
            >
              <button
                type="button"
                className="flex items-center gap-x-2 text-slate-500 px-2.5 py-1 outline-none"
              >
                {user?.ID &&
                  !isProfilePicFetching &&
                  typeof profilePicUrl === 'string' &&
                  profilePicUrl ? (
                  <img
                    src={profilePicUrl}
                    alt="profile"
                    className="size-9 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <UserCircleIcon className="size-9 cursor-pointer text-slate-600" />
                )}
                <div className="flex gap-x-1 items-center">
                  <div className="text-xs font-bold tracking-wide text-slate-700">
                    {user?.FullName || '-'}
                  </div>
                  <ChevronDown className="size-4 text-slate-800 stroke-[3px]" />
                </div>
              </button>
            </Dropdown>
          </div>
          <div className="block md:hidden">
            <button
              type="button"
              onClick={handleMenuBarClick}
              className="p-1 rounded-sm flex outline-none cursor-pointer text-slate-800 hover:text-slate-900"
              aria-label="Toggle Navigation Menu"
            >
              <Bars3Icon className="w-6 h-6 stroke-neutral-900 text-neutral-900 font-bold" />
            </button>
          </div>
        </div>
      </header>

      <ChangePasswordModal
        open={changePasswordModalVisible}
        onClose={() => setChangePasswordModalVisible(false)}
      />

      <ChangeProfilePictureModal
        open={changeProfilePictureModalVisible}
        onClose={() => setChangeProfilePictureModalVisible(false)}
        initialProfilePicUrl={profilePicUrl}
        refetchProfilePic={refetchProfilePic}
        user={user}
      />
    </>
  );
};

export default Header;
